import React, { useEffect, useState } from 'react';

import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import CreateIcon from '@material-ui/icons/Create';
import DevicesIcon from '@material-ui/icons/Devices';
import EventAvailableIcon from '@material-ui/icons/EventAvailable';
import FingerprintIcon from '@material-ui/icons/Fingerprint';
import LanguageIcon from '@material-ui/icons/Language';
import PlaceIcon from '@material-ui/icons/Place';
import VpnKeyIcon from '@material-ui/icons/VpnKey';
import Alert from '@material-ui/lab/Alert';
import { useSnackbar } from 'notistack';

import { api } from '../../services';
import { BlocoDetalhe, Campo, formatarDataHora, gradeCampos } from './List';

const textoMotivo = {
  negada: 'Permissão de localização negada',
  indisponivel: 'Localização indisponível',
  tempo_esgotado: 'Tempo esgotado ao obter a localização',
  nao_suportada: 'Navegador sem suporte à localização',
  nao_informada: 'Localização não informada',
};

function textoLocalizacao(dados) {
  if (dados.latitude === null || dados.longitude === null) {
    return textoMotivo[dados.motivo_sem_geolocalizacao] || 'Não informada';
  }
  const precisao =
    dados.precisao === null
      ? ''
      : ` (precisão ${Math.round(dados.precisao)} m)`;
  return `${dados.latitude}, ${dados.longitude}${precisao}`;
}

// Os blocos de uma assinatura (a única, ou a de cada testemunha).
const Evidencia = ({ dados, prefixo = '', rotuloAssinante = null }) => (
  <>
    <BlocoDetalhe titulo={`${prefixo}Quem assinou`}>
      <Box style={gradeCampos}>
        <Campo
          icone={<CreateIcon fontSize="small" />}
          rotulo={rotuloAssinante || (prefixo ? 'Testemunha' : 'Assinante')}
        >
          {dados.colaborador
            ? `${dados.colaborador}${
                dados.usuario ? ` (${dados.usuario})` : ''
              }`
            : dados.usuario}
        </Campo>
        <Campo
          icone={<EventAvailableIcon fontSize="small" />}
          rotulo="Data e hora"
        >
          {formatarDataHora(dados.assinado_em)}
        </Campo>
        <Campo icone={<VpnKeyIcon fontSize="small" />} rotulo="Método">
          {dados.metodo === 'clique'
            ? 'Clique no botão Assinar'
            : 'Confirmação com senha'}
        </Campo>
      </Box>
    </BlocoDetalhe>
    <BlocoDetalhe titulo={`${prefixo}Origem do acesso`}>
      <Box style={gradeCampos}>
        <Campo icone={<LanguageIcon fontSize="small" />} rotulo="IP">
          {dados.ip}
        </Campo>
        <Campo icone={<PlaceIcon fontSize="small" />} rotulo="Localização">
          {textoLocalizacao(dados)}
        </Campo>
        <Campo icone={<FingerprintIcon fontSize="small" />} rotulo="Sessão">
          {dados.sessao ? `${dados.sessao}...` : null}
        </Campo>
      </Box>
      <Box mt={2}>
        <Campo icone={<DevicesIcon fontSize="small" />} rotulo="Dispositivo">
          <span style={{ wordBreak: 'break-word' }}>{dados.user_agent}</span>
        </Campo>
      </Box>
    </BlocoDetalhe>
    <BlocoDetalhe titulo={`${prefixo}Integridade`}>
      <Campo
        icone={<FingerprintIcon fontSize="small" />}
        rotulo="SHA-256 do documento assinado"
      >
        <span style={{ wordBreak: 'break-all', fontSize: 12 }}>
          {dados.documento_sha256}
        </span>
      </Campo>
    </BlocoDetalhe>
    {dados.imagem && (
      <BlocoDetalhe titulo="Assinatura manuscrita (assinatura antiga)">
        <img
          src={dados.imagem}
          alt="Assinatura manuscrita"
          style={{
            maxWidth: '100%',
            background: '#fff',
            border: '1px solid #bbb',
          }}
        />
      </BlocoDetalhe>
    )}
  </>
);

// Evidências da assinatura de um documento (somente leitura), mostradas na aba
// do diálogo de detalhes. Os dados são pessoais: carregados só quando a aba é
// aberta e descartados ao sair dela.
const DialogoEvidencias = ({ documentoId, onErro }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [dados, setDados] = useState(null);

  useEffect(() => {
    setDados(null);
    if (!documentoId) return undefined;
    let ativo = true;
    api
      .get(`/documentos/${documentoId}/evidencias`)
      .then((response) => ativo && setDados(response.data.data))
      .catch((e) => {
        if (!ativo) return;
        enqueueSnackbar(
          (e.response && e.response.data && e.response.data.message) ||
            'Erro ao carregar as evidências.',
          { variant: 'error' },
        );
        onErro();
      });
    return () => {
      ativo = false;
    };
  }, [documentoId]);

  if (!dados) return <Typography variant="body2">Carregando...</Typography>;

  // Assinatura por testemunhas: um conjunto de evidências por testemunha.
  if (dados.forma_assinatura === 'varias') {
    return (
      <>
        <Alert
          severity={dados.assinaturas.length < dados.total ? 'warning' : 'info'}
        >
          {`Assinaturas: ${dados.assinaturas.length} de ${dados.total}`}
        </Alert>
        {[...dados.assinaturas]
          .sort((a, b) => a.ordem - b.ordem)
          .map((assinatura) => (
            <Evidencia
              key={assinatura.ordem}
              dados={assinatura}
              prefixo={`Assinatura ${assinatura.ordem} · `}
              rotuloAssinante="Assinante"
            />
          ))}
      </>
    );
  }
  if (dados.assinaturas) {
    return (
      <>
        <Alert severity="info">
          {`Assinado por testemunhas. Motivo: ${dados.motivo || '—'}`}
        </Alert>
        {dados.assinaturas.map((assinatura, indice) => {
          // Pessoas fixas assinam ao lado das testemunhas.
          const fixa = assinatura.papel !== 'testemunha';
          return (
            <Evidencia
              key={assinatura.usuario || indice}
              dados={assinatura}
              prefixo={
                fixa
                  ? `Assinatura ${assinatura.ordem} · `
                  : `Testemunha ${indice + 1} · `
              }
              rotuloAssinante={fixa ? 'Assinante' : null}
            />
          );
        })}
      </>
    );
  }
  return <Evidencia dados={dados} />;
};

export default DialogoEvidencias;
